import { Box, Button, Flex, Heading, Spinner, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, FormikHelpers } from "formik";
import { InputControl } from "formik-chakra-ui";
import React, { useEffect, useMemo, useState } from "react";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import {
  useGetFormRule,
  useGetFormRuleKey,
} from "src/generated/hooks/form-service/queries";
import {
  createFormRule,
  updateFormRule,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import {
  generateKey,
  isUserGeneratedKey,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import {
  GET_BOUNDARIES_FOR_RULE_CONFIGURATION,
  GET_FORM_ENGINE_RULE_QUESTIONS,
} from "src/scenes/orgAdmin/formTemplates/RulesTab/queries";
import { validateWithZod } from "src/services/formValidations";
import {
  RawFormEngineRule,
  RuleConfigurationSchema,
  RuleType,
} from "src/types/formEngineRule";
import * as GQL from "src/types/graphql";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";
import * as RD from "src/types/remoteData";
import { AutoDecision } from "./AutoDecision";
import { AutoPlacement } from "./AutoPlacement";
import { AutoTag } from "./AutoTag";
import {
  emptyRuleValidationState,
  toRuleValidationState,
  ValidationContextProvider,
} from "./context";
import { flattenFormTemplateQuestions } from "./helpers";
import {
  ConfigurationType,
  FormEngineRuleFormData,
  FormEngineRuleFormSchema,
} from "./schemas";
import { buildRulePayload, School, TagType } from "./shared";

const defaultValues: FormEngineRuleFormData = {
  tag_ids: [],
  school_ids: [],
  status: [],
  enable_remove_tag: true,
  rule_name: "",
  rule_key: "",
  userGeneratedKey: false,
  rule_description: "",
  rule_type: undefined,
  configuration_type: undefined,
  question_id: null,
  answer_option_ids: [],
  inside_outside: null,
  boundary_type: null,
  boundary_ids: [],
  source_statuses: [],
  enable_automatic_offers: false,
  offer_from_waitlist_only: false,
};

const makeInitialValues = (
  rule: (RawFormEngineRule & { id: string }) | null
): FormEngineRuleFormData | undefined => {
  if (!rule) return undefined;
  try {
    const rule_type =
      (rule.rule_type as GQL.form_engine_rule_type_enum) ?? undefined;

    const config = RuleConfigurationSchema.safeParse({
      ...JSON.parse(rule.rule_config),
      rule_type,
    });

    if (!config.success) {
      console.error("makeInitialValues: cannot parse rule");
      return undefined;
    }

    const getConfigurationType = (
      rule_type: GQL.form_engine_rule_type_enum | undefined
    ): ConfigurationType => {
      switch (rule_type) {
        case GQL.form_engine_rule_type_enum.DYNAMIC_SIBLING_TAG:
          return "DYNAMIC_SIBLING";
        case GQL.form_engine_rule_type_enum.QUESTION_TAG:
        case GQL.form_engine_rule_type_enum.GEOGRAPHIC_PREFERENCE_TAG:
          return "FORM_QUESTION";
        case GQL.form_engine_rule_type_enum.AUTO_WAITLIST:
          return "AUTO_WAITLIST";
        case GQL.form_engine_rule_type_enum.AUTO_PLACEMENT:
          return "AUTO_PLACEMENT";
        default:
          return undefined;
      }
    };

    const ruleName = config.data.rule_name ?? "";
    const existingKey = rule.key ?? "";
    const ruleKey =
      existingKey.trim().length > 0 ? existingKey : generateKey(ruleName);

    const base = {
      configuration_type: getConfigurationType(rule_type),
      id: rule.id,
      rule_name: ruleName,
      rule_key: ruleKey,
      userGeneratedKey: isUserGeneratedKey(ruleKey, ruleName),
      rule_description: config.data.rule_description ?? "",
      rule_type,
    };

    switch (config.data.rule_type) {
      case GQL.form_engine_rule_type_enum.AUTO_WAITLIST:
        return {
          ...base,
          tag_ids: [],
          enable_remove_tag: false,
          school_ids: config.data.school_ids ?? [],
          status: [],
          question_id: null,
          answer_option_ids: [],
          inside_outside: null,
          boundary_type: null,
          boundary_ids: [],
          source_statuses: config.data.source_statuses,
          enable_automatic_offers: config.data.enable_automatic_offers,
          offer_from_waitlist_only: config.data.offer_from_waitlist_only,
        };
      case GQL.form_engine_rule_type_enum.AUTO_PLACEMENT:
        return {
          ...base,
          tag_ids: config.data.tag_ids ?? [],
          enable_remove_tag: false,
          school_ids: config.data.school_ids ?? [],
          status: [],
          question_id: null,
          answer_option_ids: [],
          inside_outside: null,
          boundary_type: null,
          boundary_ids: [],
          source_statuses: config.data.source_statuses,
          enable_automatic_offers: false,
          offer_from_waitlist_only: false,
        };
      case GQL.form_engine_rule_type_enum.DYNAMIC_SIBLING_TAG:
        return {
          ...base,
          tag_ids: config.data.tag_ids ?? [],
          enable_remove_tag: config.data.enable_remove_tag ?? false,
          school_ids: config.data.school_ids,
          status: config.data.status,
          question_id: null,
          answer_option_ids: [],
          inside_outside: null,
          boundary_type: null,
          boundary_ids: [],
          source_statuses: [],
          enable_automatic_offers: false,
          offer_from_waitlist_only: false,
        };
      case GQL.form_engine_rule_type_enum.QUESTION_TAG:
        return {
          ...base,
          tag_ids: config.data.tag_ids ?? [],
          enable_remove_tag: config.data.enable_remove_tag ?? false,
          school_ids: config.data.school_ids ?? [],
          status: [],
          question_id: config.data.question_id,
          answer_option_ids: config.data.answer_option_ids,
          inside_outside: null,
          boundary_type: null,
          boundary_ids: [],
          source_statuses: [],
          enable_automatic_offers: false,
          offer_from_waitlist_only: false,
        };
      case GQL.form_engine_rule_type_enum.GEOGRAPHIC_PREFERENCE_TAG:
        return {
          ...base,
          tag_ids: config.data.tag_ids ?? [],
          enable_remove_tag: config.data.enable_remove_tag ?? false,
          school_ids: config.data.school_ids ?? [],
          status: [],
          question_id: config.data.question_id,
          answer_option_ids: [],
          inside_outside: config.data.is_inside ? "inside" : "outside",
          boundary_type: config.data.boundary_type,
          boundary_ids: config.data.boundary_ids,
          source_statuses: [],
          enable_automatic_offers: false,
          offer_from_waitlist_only: false,
        };
      default:
        return undefined;
    }
  } catch {
    return undefined;
  }
};

export interface FormEngineRuleDrawerProps {
  hasPrograms: boolean;
  organizationId: string;
  rule: { id: string } | null;
  schools: School[];
  tags: TagType[];
  unavailableSchoolIds: string[];
  componentId: string;
  formTemplate: GQL.EditFormTemplateFragment;
  initialRuleType: RuleType | null;
  isOpen: boolean;
  onClose: () => void;
  setIsFormEngineRulePanelOpen: (state: boolean) => void;
  setFormEngineRuleToEdit: (
    rule: {
      id: string;
    } | null
  ) => void;
  refetch: () => Promise<void>;
}

export const FormEngineRuleDrawer: React.FC<FormEngineRuleDrawerProps> = ({
  hasPrograms,
  organizationId,
  formTemplate,
  rule,
  schools,
  tags,
  unavailableSchoolIds,
  componentId,
  initialRuleType,
  isOpen,
  onClose,
  setIsFormEngineRulePanelOpen,
  setFormEngineRuleToEdit,
  refetch,
}) => {
  /*
    context
  */
  const toast = useAvelaToast();
  const queryClient = useQueryClient();

  /*
    state
  */
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState(emptyRuleValidationState);
  const isEditMode: boolean = !!rule;

  /*
    model
  */

  const ruleQuery = useGetFormRule(
    {
      path: {
        formTemplateId: formTemplate.id,
        id: rule?.id ?? "",
      },
    },
    [formTemplate.id, rule?.id],
    { enabled: !!rule?.id && isOpen }
  );

  const isRuleLoading =
    isEditMode && (ruleQuery.isLoading || ruleQuery.isFetching);

  const isRuleLoadError = useMemo(() => {
    if (!isEditMode || isRuleLoading) {
      return false;
    }

    if (ruleQuery.isError) {
      return true;
    }

    return ruleQuery.isSuccess && !ruleQuery.data?.data;
  }, [
    isEditMode,
    isRuleLoading,
    ruleQuery.isError,
    ruleQuery.isSuccess,
    ruleQuery.data,
  ]);

  const drawerIsOpen =
    isOpen &&
    (!isEditMode ||
      isRuleLoading ||
      (ruleQuery.isSuccess && !!ruleQuery.data?.data));

  useEffect(() => {
    if (!isOpen || !isRuleLoadError) {
      return;
    }

    toast.error({
      title: "Failed to load rule",
      description: "The rule could not be retrieved. Please try again.",
    });
    onClose();
  }, [isOpen, isRuleLoadError, toast, onClose]);

  const remoteData = useMemo(() => RD.fromRestResult(ruleQuery), [ruleQuery]);

  const { remoteData: formTemplateQuestionsRD } = useRemoteDataQuery<
    GQL.GetFormEngineRuleQuestions,
    GQL.GetFormEngineRuleQuestionsVariables
  >(GET_FORM_ENGINE_RULE_QUESTIONS, {
    variables: {
      formTemplateId: formTemplate.id,
    },
    fetchPolicy: "no-cache",
  });

  const { remoteData: boundariesRD } = useRemoteDataQuery<
    GQL.GetBoundariesForRuleConfiguration,
    GQL.GetBoundariesForRuleConfigurationVariables
  >(GET_BOUNDARIES_FOR_RULE_CONFIGURATION, {
    variables: {
      organizationId: organizationId,
    },
    fetchPolicy: "no-cache",
  });

  const loadedRule = ruleQuery.data?.data ?? null;

  const formInitialValues = useMemo((): FormEngineRuleFormData => {
    if (loadedRule) {
      return makeInitialValues(loadedRule) ?? defaultValues;
    }

    if (initialRuleType === "auto_waitlist") {
      return {
        ...defaultValues,
        configuration_type: "AUTO_WAITLIST",
        source_statuses: [GQL.form_status_enum.Admissions],
      };
    }

    if (initialRuleType === "auto_placement") {
      return {
        ...defaultValues,
        configuration_type: "AUTO_PLACEMENT",
        rule_type: GQL.form_engine_rule_type_enum.AUTO_PLACEMENT,
        source_statuses: [GQL.form_status_enum.Submitted],
      };
    }

    return defaultValues;
  }, [loadedRule, initialRuleType]);

  useEffect(() => {
    if (isOpen) {
      setValidation(emptyRuleValidationState);
    }
  }, [isOpen, rule?.id]);

  useEffect(() => {
    setIsLoading(
      remoteData.isLoading() ||
        formTemplateQuestionsRD.isLoading() ||
        boundariesRD.isLoading()
    );
  }, [setIsLoading, remoteData, formTemplateQuestionsRD, boundariesRD]);

  const flattenedQuestions = useMemo(() => {
    return formTemplateQuestionsRD.hasData()
      ? flattenFormTemplateQuestions(
          formTemplateQuestionsRD.data.form_template_by_pk
        )
      : [];
  }, [formTemplateQuestionsRD]);

  const submitForm = async (
    values: FormEngineRuleFormData,
    formikHelpers: FormikHelpers<FormEngineRuleFormData>
  ) => {
    try {
      setValidation(emptyRuleValidationState);
      formikHelpers.setErrors({});

      const payload = buildRulePayload(values);
      if (!payload) {
        toast.error({
          title: "Rule is incomplete",
          description: "Finish configuring the rule before saving.",
        });
        return;
      }

      const response = values.id
        ? await updateFormRule({
            path: { formTemplateId: formTemplate.id, id: values.id },
            body: payload,
          })
        : await createFormRule({
            path: { formTemplateId: formTemplate.id },
            body: payload,
          });

      const outcome = response.data?.data;
      if (outcome?.result === "invalid") {
        setValidation(toRuleValidationState(outcome.validationResults));
        toast.error({
          title: "Unable to save this rule",
          description: "Fix the highlighted fields and try again.",
        });

        return;
      }

      if (!outcome || outcome.result !== "success") {
        toast.error({
          title: "Oops! Something went wrong",
          description: "Failed to save rule",
        });
        return;
      }

      const ruleTypeLabel =
        values.configuration_type === "DYNAMIC_SIBLING"
          ? "Dynamic sibling"
          : values.configuration_type === "FORM_QUESTION"
          ? "Question"
          : values.configuration_type === "AUTO_WAITLIST"
          ? "Auto Waitlist"
          : values.configuration_type === "AUTO_PLACEMENT"
          ? "Auto placement"
          : "Form engine";

      toast({
        title: "Success",
        description: `${ruleTypeLabel} rule ${
          values.id ? "updated" : "created"
        }`,
        status: "success",
      });

      setIsFormEngineRulePanelOpen(false);
      setFormEngineRuleToEdit(null);
      onClose();
      await refetch();

      if (isEditMode) {
        void queryClient.invalidateQueries({
          queryKey: [useGetFormRuleKey],
        });
      }
    } catch (error) {
      console.error(error);
      toast.error({
        title: "Oops! Something went wrong",
        description: "Failed to save rule",
      });
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  /*
    elements
  */
  const headerContent = (
    <Heading as="h3" size="1.25rem">
      {isEditMode ? "Edit rule" : "Create rule"}
    </Heading>
  );
  const footerContent = (
    <>
      <Button
        variant="outline"
        mr={3}
        onClick={onClose}
        width="50%"
        colorScheme="gray"
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form={componentId}
        colorScheme="primary"
        isLoading={isLoading}
        width="50%"
      >
        Save
      </Button>
    </>
  );

  /*
    component
  */

  return (
    <ResizableDrawer
      isOpen={drawerIsOpen}
      placement="right"
      onClose={onClose}
      size="lg"
      header={headerContent}
      footer={footerContent}
      autosaveId="dynamic-sibling-rule-panel-width"
    >
      <Box>
        {isLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            flex="1"
          >
            <Spinner color="primary.500" size="lg" />
          </Box>
        ) : (
          <Formik<FormEngineRuleFormData>
            initialValues={formInitialValues}
            validate={validateWithZod(FormEngineRuleFormSchema)}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (values, formikHelpers) => {
              await submitForm(values, formikHelpers);
            }}
          >
            {(formik) => {
              const isAutoDecision =
                initialRuleType === "auto_waitlist" ||
                formik.values.configuration_type === "AUTO_WAITLIST";
              const isAutoPlacement =
                initialRuleType === "auto_placement" ||
                formik.values.configuration_type === "AUTO_PLACEMENT";
              const isAutoTag =
                initialRuleType === "auto_tag" ||
                formik.values.configuration_type === "DYNAMIC_SIBLING";

              let placeholder = {
                rule_name: "E.g.: Rule name",
                rule_key: "E.g.: rule-key",
                rule_description: "E.g.: Rule description",
              };

              if (isAutoPlacement) {
                placeholder = {
                  rule_name: "E.g.: Auto placement",
                  rule_key: "E.g.: auto-placement",
                  rule_description:
                    "E.g.: Automatically place students at top ranked school upon form submission",
                };
              } else if (isAutoDecision) {
                placeholder = {
                  rule_name: "E.g.: Auto waitlist and offer",
                  rule_key: "E.g.: auto-waitlist-and-offer",
                  rule_description:
                    "E.g.: Automatically add to waitlist and offer by capacity",
                };
              } else if (isAutoTag) {
                placeholder = {
                  rule_name: "E.g.: Dynamic sibling",
                  rule_key: "E.g.: dynamic-sibling",
                  rule_description: "E.g.: Tag forms based on siblings",
                };
              }

              formik.setErrors({ ...formik.errors, ...validation.fieldErrors });

              return (
                <Form id={componentId}>
                  <Flex gap="1.5rem" pb="1.5rem" direction="column">
                    <InputControl
                      name="rule_name"
                      label="Rule name"
                      labelProps={{ p: "0", m: "0", color: "gray.700" }}
                      inputProps={{
                        placeholder: placeholder.rule_name,
                        autoComplete: "off",
                        ...PASSWORD_MANAGER_IGNORE,
                        onChange: (event) => {
                          const name = event.target.value;
                          void formik.setFieldValue("rule_name", name);
                          if (!formik.values.userGeneratedKey) {
                            void formik.setFieldValue(
                              "rule_key",
                              generateKey(name)
                            );
                          }
                        },
                      }}
                    />
                    <InputControl
                      name="rule_key"
                      label="Rule key"
                      labelProps={{ p: "0", m: "0", color: "gray.700" }}
                      inputProps={{
                        placeholder: placeholder.rule_key,
                        autoComplete: "off",
                        ...PASSWORD_MANAGER_IGNORE,
                        onChange: (event) => {
                          const key = event.target.value;
                          void formik.setFieldValue("rule_key", key);
                          void formik.setFieldValue(
                            "userGeneratedKey",
                            key.trim().length > 0
                          );
                        },
                      }}
                    />
                    <InputControl
                      name="rule_description"
                      label="Rule description (optional)"
                      labelProps={{ p: "0", m: "0", color: "gray.700" }}
                      inputProps={{
                        placeholder: placeholder.rule_description,
                        autoComplete: "off",
                        ...PASSWORD_MANAGER_IGNORE,
                      }}
                    />
                  </Flex>

                  <ValidationContextProvider value={validation}>
                    {/* AUTO WAITLIST - Completely separate layout */}
                    {formik.values.configuration_type === "AUTO_WAITLIST" ? (
                      <AutoDecision
                        hasPrograms={hasPrograms}
                        formik={formik}
                        schools={schools}
                      />
                    ) : formik.values.configuration_type ===
                      "AUTO_PLACEMENT" ? (
                      <AutoPlacement
                        formik={formik}
                        schools={schools}
                        tags={tags}
                        unavailableSchoolIds={unavailableSchoolIds}
                      />
                    ) : (
                      /* AUTO TAG - Original tag-based layout */
                      <AutoTag
                        formik={formik}
                        questions={flattenedQuestions}
                        boundaries={
                          boundariesRD.hasData()
                            ? boundariesRD.data.boundaries
                            : []
                        }
                        tags={tags}
                        schools={schools}
                      />
                    )}
                  </ValidationContextProvider>

                  {validation.generalErrors.length > 0 && (
                    <Flex direction="column" gap="0.25rem" pt="0.75rem">
                      {validation.generalErrors.map((message, index) => (
                        <Text
                          key={`general-error-${index}`}
                          fontSize="sm"
                          color="red.500"
                        >
                          {message}
                        </Text>
                      ))}
                    </Flex>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </Box>
    </ResizableDrawer>
  );
};
